import { getBinFromCreditCardNumber } from "@kushki/js/lib/service/UtilsService";
import { SubscriptionCardAsyncTokenRequest } from "@kushki/js/lib/types/subscription_card_async_token_request";
import { ErrorMessage } from "components/Error/ErrorMessageComponent";
import { Input } from "components/Input/InputComponent";
import { Footer } from "components/UI/Footer/FooterComponent";
import { PayButton } from "components/UI/PayButton/PayButtonComponent";
import { getCardBrand } from "containers/Card/store/Actions";
import {
  getCardSubscriptionAsyncToken,
  setDirty,
  updateInput,
} from "containers/CardSubscriptionAsync/store/ActionCreators";
import { get, isEmpty, set } from "lodash";
import * as React from "react";
import {
  LocalizeContextProps,
  Translate,
  withLocalize,
} from "react-localize-redux";
import { connect } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import { Dispatch } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { sendParametersToParent } from "root/shared/Messenger";
import { PaymenMethodEnum } from "root/shared/PaymenMethodEnum";
import { PciImages } from "root/shared/PciImages";
import { calculateTotalAmount, checkValidity } from "root/shared/Utility";
import { CajitaStore, IInputProps } from "root/store/CajitaStore";
import { ICardSubscriptionAsyncActionCreator } from "types/card_subscription_action_creator";
import { ICardSubscriptionAsyncFormProps } from "types/card_subscription_async_form_props";
import { ITransferAmount } from "types/transfer_amount";
import "./assets/css/styles.css";

/**
 * CardSubscriptionAsync StateFull Component
 */
class CardSubscriptionAsync extends React.Component<
  ICardSubscriptionAsyncFormProps & LocalizeContextProps
> {
  private inputCard: IInputProps | null;

  constructor(props: ICardSubscriptionAsyncFormProps & LocalizeContextProps) {
    super(props);
    this.inputCard = null;
    this.props.addTranslation(this.props.translation);
    this._inputHandler = this._inputHandler.bind(this);
    this._submitHandler = this._submitHandler.bind(this);
    this._cardNumberHandler = this._cardNumberHandler.bind(this);
    this._getBrandCard = this._getBrandCard.bind(this);
  }

  public componentDidUpdate(): void {
    // istanbul ignore else
    if (this.props.token !== undefined && this.props.token !== "")
      sendParametersToParent({
        token: this.props.token,
        paymentMethod: PaymenMethodEnum.CARD_SUBSCRIPTION_ASYNC,
      });

    if (this.inputCard !== null && this.inputCard.hasOwnProperty("classes")) {
      const classes: string[] = ["input", "input-cardNumber"];

      this.inputCard.classContainer = "inputContainer cardNumber-input top";
      this.inputCard.classes = classes;

      if (this.props.brand.length > 0) {
        this.inputCard.classContainer = "inputContainer top";
        this.inputCard.classes.push(this.props.brand);
        this.props.onChangeInput(this.inputCard);
      }
    }

    return;
  }

  public render(): React.ReactNode {
    return !this.props.isCardSubscriptionAsyncAvailable ? (
      <Container>
        <Row className={"text-center"}>
          <Col>
            <h4>
              <Translate id="notAvailableCardSubscriptionAsync" />
            </h4>
          </Col>
        </Row>
        <div className="section">
          <div className={`card-async-${PciImages.webpayOneClickImage}`} />
        </div>
        <div className={"poweredBy"} />
      </Container>
    ) : (
      <form onSubmit={this._submitHandler} autoComplete="off" noValidate={true}>
        <ErrorMessage />
        <div className="card-subscription-async-container">
          <div className="section">
            <div className={`card-async-${PciImages.webpayOneClickImage}`} />
          </div>
          <div className="section">
            <Row>
              <Col>
                <Input
                  {...this.props.cardNumber}
                  change={this._cardNumberHandler}
                  touched={this.props.onTouched}
                />
              </Col>
            </Row>
          </div>
          <div className="section">
            <Row>
              <Col>
                <Input
                  {...this.props.emailInput}
                  change={this._inputHandler}
                  touched={this.props.onTouched}
                />
              </Col>
            </Row>
          </div>
          <PayButton
            isValid={this.props.isValid}
            totalAmount={this.props.amount}
            isSubscription={true}
            currency={this.props.currency}
            disable={this.props.disablePayButton}
          />
        </div>
        <Footer />
      </form>
    );
  }

  private _inputHandler(id: string, value: string): void {
    const new_input: IInputProps = { ...this.props[id] };

    new_input.valid = checkValidity(value, new_input.rules);
    new_input.value = value;

    this.props.onChangeInput(new_input);

    return;
  }

  private _cardNumberHandler(id: string, value: string): void {
    const new_input: IInputProps = { ...this.props[id] };

    if (value.length === 9 || value.length > 18) this._getBrandCard(value);

    new_input.valid = checkValidity(value, new_input.rules);
    new_input.value = value;

    this.inputCard = new_input;

    this.props.onChangeInput(new_input);
  }

  private _getBrandCard(cardBin: string): void {
    this.props.cardBrand(getBinFromCreditCardNumber(cardBin));
  }

  private _submitHandler(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    const cardAsyncTokenRequest: SubscriptionCardAsyncTokenRequest = {
      currency: this.props.currency,
      email: this.props.emailInput.value,
      amount: this.props.amount,
      callbackUrl: this.props.callbackUrl,
    };

    if (!isEmpty(get(this.props, "cardNumber.value")))
      set(
        cardAsyncTokenRequest,
        "cardNumber",
        get(this.props, "cardNumber.value")
          .split(get(this.props, "cardNumber.separator", " "))
          .join("")
      );

    this.props.requestToken(cardAsyncTokenRequest);
  }
}

const mapStateToProps: (
  state: CajitaStore
) => ICardSubscriptionAsyncFormProps = (
  state: CajitaStore
): ICardSubscriptionAsyncFormProps => ({
  ...state.card_subscription_async,
  currency: state.config.currency as "CLP" | "UF",
  amount:
    typeof state.config.amount === "object"
      ? calculateTotalAmount(state.config.amount as ITransferAmount)
      : state.config.amount,
  callbackUrl: state.config.callback_url,
  brand: state.card.brand,
  disablePayButton: state.config.disablePayButton,
});

const mapDispatchToProps: (dispatch: Dispatch) => object = (
  dispatch: ThunkDispatch<
    CajitaStore,
    undefined,
    ICardSubscriptionAsyncActionCreator
  >
): object => ({
  cardBrand: (binNumber: string): void => dispatch(getCardBrand(binNumber)),
  onChangeInput: (payload: IInputProps): void => dispatch(updateInput(payload)),
  onTouched: (payload: IInputProps): ICardSubscriptionAsyncActionCreator =>
    dispatch(setDirty(payload)),
  requestToken: (payload: SubscriptionCardAsyncTokenRequest): void =>
    dispatch(getCardSubscriptionAsyncToken(payload)),
});

const CardSubscriptionAsyncComponent: React.ComponentClass = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withLocalize<ICardSubscriptionAsyncFormProps & LocalizeContextProps>(
    CardSubscriptionAsync
  )
);

export { CardSubscriptionAsyncComponent };



// WEBPACK FOOTER //
// ./src/containers/CardSubscriptionAsync/CardSubscriptionAsyncComponent.tsx